import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Tag, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PROMO_IMG =
  "https://images.unsplash.com/photo-1621712151262-60bd142ba19f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export const Promotions = () => {
  const [promos, setPromos] = useState([]);

  useEffect(() => {
    api.get("/promotions").then((r) => setPromos(r.data)).catch(() => {});
  }, []);

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Code ${code} copied`, { description: "Show it in-store or mention on the call." });
  };

  return (
    <section id="promotions" className="relative py-20 sm:py-28 bg-background z-10" data-testid="promotions">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead index="01" kicker="Current Promotions" title="Deals Worth The Drive" />

        <div className="grid lg:grid-cols-12 gap-6 mt-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 relative min-h-[320px] overflow-hidden border border-border group"
          >
            <img src={PROMO_IMG} alt="Premium wheel" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
            <div className="absolute bottom-0 p-8">
              <span className="text-xs font-bold tracking-[0.3em] uppercase text-primary">Featured</span>
              <h3 className="font-display font-black text-3xl uppercase tracking-tight mt-2">Performance Season</h3>
              <p className="text-foreground/70 text-sm mt-2 max-w-xs">Summer grip that holds the apex. Limited stock on flagship compounds.</p>
            </div>
          </motion.div>

          <div className="lg:col-span-7 grid gap-6">
            {promos.map((p, i) => (
              <motion.div
                key={p.code}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex items-center justify-between gap-4 border border-border bg-card p-6 sm:p-8 hover:border-primary/60 transition-colors"
                data-testid={`promo-${p.code}`}
              >
                <div className="flex items-start gap-4">
                  <div className="mt-1 text-primary"><Tag size={26} weight="duotone" /></div>
                  <div>
                    <h3 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight">{p.title}</h3>
                    <p className="text-muted-foreground text-sm mt-1">{p.subtitle}</p>
                    <p className="text-xs text-primary/80 mt-2 tracking-wide uppercase">{p.expires}</p>
                  </div>
                </div>
                <button
                  onClick={() => copy(p.code)}
                  className="shrink-0 flex items-center gap-2 border border-primary text-primary font-bold text-xs uppercase tracking-wide px-4 py-3 hover:bg-primary hover:text-white transition-colors rounded-sm"
                  data-testid={`promo-copy-${p.code}`}
                >
                  {p.code} <ArrowRight size={16} weight="bold" />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const SectionHead = ({ index, kicker, title, light }) => (
  <div className="flex flex-col gap-3">
    <div className="flex items-center gap-3">
      <span className={`font-display font-black text-lg ${light ? "text-black/40" : "text-primary"}`}>[{index}]</span>
      <span className={`text-xs font-bold tracking-[0.3em] uppercase ${light ? "text-black/60" : "text-muted-foreground"}`}>{kicker}</span>
    </div>
    <motion.h2
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className={`font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl ${light ? "text-black" : "text-foreground"}`}
    >
      {title}
    </motion.h2>
  </div>
);
